// Every task on the platform, dense and re-read on a timer. The cockpit
// view of "what is the system doing right now" -- the HUD in the shell
// carries the totals, this carries the rows behind them.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminListTasks, type TaskView } from "../../api/client";
import { formatMinor, formatWhen } from "../../lib/format";

const STATES = ["", "QUEUED", "RUNNING", "DONE", "FAILED", "CANCELLED"];
const REFRESH_MS = 5000;

export default function AdminTasks() {
  const [tasks, setTasks] = useState<TaskView[]>([]);
  const [state, setState] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [at, setAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    try {
      const t = await adminListTasks({ state: state || undefined, limit: 200 });
      setTasks(t);
      setAt(new Date());
      setError(null);
    } catch (e) {
      // A failed re-read keeps the last good table on screen and says so,
      // rather than blanking the view on one dropped request.
      setError(e instanceof Error ? e.message : "failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [state]);

  useEffect(() => {
    setLoading(true);
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  return (
    <>
      <div className="a-head">
        <div>
          <h1 className="a-h1">Tasks</h1>
          <p className="a-lede">
            All tasks, newest first. The server caps this list at 200 rows; the HUD above reports
            when a count hits that cap.
          </p>
        </div>
        <span className="a-panel__meta">{at ? `read ${at.toLocaleTimeString()}` : "reading…"}</span>
      </div>

      <div className="a-tabs">
        {STATES.map((s) => (
          <button key={s} className={s === state ? "a-tab a-tab--on" : "a-tab"} onClick={() => setState(s)}>
            {s || "all"}
          </button>
        ))}
      </div>

      {error && <p className="a-error">{error}</p>}

      <div className="a-panel">
        <div className="a-panel__head">
          <span className="a-panel__title">Task queue</span>
          <span className="a-panel__meta">{tasks.length} rows</span>
        </div>
        {loading ? (
          <p className="a-loading">Loading…</p>
        ) : tasks.length === 0 ? (
          <p className="a-empty">No tasks in this state.</p>
        ) : (
          <div className="a-scroll">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Alias</th>
                  <th>Class</th>
                  <th>State</th>
                  <th className="a-table__num">Done</th>
                  <th className="a-table__num">Failed</th>
                  <th className="a-table__num">Cancelled</th>
                  <th className="a-table__num">Quoted</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.task_id}>
                    <td>
                      <Link to={`/admin/tasks/${t.task_id}`}>{t.task_id.slice(0, 8)}</Link>
                    </td>
                    <td>{t.alias}</td>
                    <td className="a-table__dim">{t.scheduling_class}</td>
                    <td>
                      <span className={`a-state a-state--${t.state.toLowerCase()}`}>{t.state.toLowerCase()}</span>
                    </td>
                    <td className="a-table__num">
                      {t.done} / {t.requested}
                    </td>
                    <td className="a-table__num">{t.failed || "·"}</td>
                    <td className="a-table__num">{t.cancelled || "·"}</td>
                    <td className="a-table__num">{formatMinor(t.quoted_total, t.currency)}</td>
                    <td className="a-table__dim">{formatWhen(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
